import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { CUSTOMER_JSON_POLL_INTERVAL } from "constants/intervals";
import useQueryWithErrorHandling from "utils/useQuery";
import {
  EMPTY_FLIGHT_CONFIG,
  FlightConfigType,
  FLIGHT_CONFIG_QUERY,
  parseFlightConfig,
  useFlightConfig,
  useIsFlightConfigInitialized,
} from "./useFlightConfig";
import { midgardClients } from "providers/MidgardProvider";
import { useState } from "react";
import { useEffect } from "react";
import { useRef } from "react";
import { useInterval } from "react-use";
import { INVALID_CUSTOMERS } from "constants/constants";

const TRY_AGAIN_AFTER_TIMEOUT = 5 * 1000;
const FLIGHT_CONFIG_POLL_INTERVAL = 5 * 60 * 1000;

/** fetch the flight config once we have the ckey */
export const FlightConfigGetter = () => {
  const ckey = useCustomerKey();

  const ready = ckey && !INVALID_CUSTOMERS.includes(ckey);

  // ? it appears that sometimes flightConfig is never fetched... (e.g. on reports page https://localhost:3000/raveng/reports/eos/All?ge=1705363200000&lt=1705406400000&workSchedule=1 - refresh multiple times to replicate, appears to occur ~1/3 of the time)
  // hack-fix: if flightConfig is still not found after N seconds of fetching, try again once
  const [toggle, setToggle] = useState(true);
  const [isFlightConfigInitialized] = useIsFlightConfigInitialized();
  const timer = useRef(0);

  useEffect(() => {
    const cancel = () => {
      window.clearTimeout(timer.current);
      timer.current = 0;
    };
    if (!timer.current) {
      timer.current = window.setTimeout(() => {
        if (ready && isFlightConfigInitialized !== "initialized") {
          console.log("⚠ flight config was not fetched, trying again...");
          setToggle(false);
          setTimeout(() => {
            setToggle(true);
          });
        } else if (isFlightConfigInitialized === "initialized") {
          cancel();
        }
      }, TRY_AGAIN_AFTER_TIMEOUT);
    }
    return isFlightConfigInitialized !== "initialized" ? cancel : () => null;
  }, [ready, isFlightConfigInitialized]);

  // refetch flightConfig on interval
  useInterval(
    () => {
      setToggle(false);
      setTimeout(() => {
        setToggle(true);
      });
    },
    isFlightConfigInitialized === "initialized"
      ? FLIGHT_CONFIG_POLL_INTERVAL
      : null
  );

  return ready && toggle ? <FlightConfigQuery /> : null;
};

export function FlightConfigQuery({
  customOnCompleted,
  ckey: ckeyProp,
  queryProps,
}: {
  customOnCompleted?: (data: any) => void;
  ckey?: string;
  queryProps?: any;
}) {
  const [flightConfig, setFlightConfig] = useFlightConfig();
  const [, setIsFlightConfigInitialized] = useIsFlightConfigInitialized();
  const ckeyState = useCustomerKey();
  const ckey = ckeyProp || ckeyState;
  useQueryWithErrorHandling("MIDGARD_FLIGHT_CONFIG", FLIGHT_CONFIG_QUERY, {
    client: midgardClients.get(ckey),
    pollInterval: customOnCompleted
      ? 0
      : CUSTOMER_JSON_POLL_INTERVAL *
        flightConfig.general.multiplyNetworkRequestIntervals,
    onCompleted: data => {
      if (customOnCompleted) {
        customOnCompleted(data);
        return;
      }
      // only delete if the customer is different (otherwise, we should try to use the flightConfig we already have)
      const shouldDeletePrev = !flightConfig.ckey || flightConfig.ckey !== ckey;
      const handleDeletePrev = () => {
        if (shouldDeletePrev) {
          setIsFlightConfigInitialized("uninitialized");
          setFlightConfig({ ...EMPTY_FLIGHT_CONFIG, ckey });
        }
      };
      if (!data?.flightConfig) {
        console.error("No flightConfig found");
        console.log(data);
        handleDeletePrev();
        return;
      }
      try {
        const flightConfigParsed = parseFlightConfig(data.flightConfig);
        console.log("received flight config:", flightConfigParsed);
        setFlightConfig({ ...flightConfigParsed, ckey } as FlightConfigType);
        setIsFlightConfigInitialized("initialized");
      } catch (err) {
        console.error("Error parsing flightConfig", err);
        handleDeletePrev();
      }
    },
    ...queryProps,
  });
  return null;
}
// TODO test what happens when flightConfig request errors
